import { LanguagesConfig, Recipe, RecipeTag } from '@/api/types'
import { faTags } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { UseMutationResult } from '@tanstack/react-query'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import React from 'react'
import { SortDescriptor } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { components } from 'react-select'

import { Select } from '@/components/Select'
import {
  Column,
  Table,
  TableBody,
  TableHeader,
  TablePagination,
  TableSearch,
} from '@/components/Table'
import { groupTags } from '../helpers'
import './RecipesTable.css'
import { RecipeRow } from './TableRecipeRow'

const routeApi = getRouteApi('/recipes')

type Props = {
  recipes: Recipe[]
  totalCount: number
  tags: RecipeTag[]
  saveRecipe: UseMutationResult
  languages?: LanguagesConfig
}

export default function RecipesTable({ recipes, totalCount, tags, saveRecipe, languages }: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const searchParams = routeApi.useSearch()
  let sortField
  let sortDirection
  if (searchParams.sortField === 'ENERGY_PER_SERVING') {
    sortField = 'caloriesPerServing'
    sortDirection = searchParams.sortDirection === 'ASC' ? 'ascending' : 'descending'
  }

  const groupedTags = groupTags(tags)

  const lang = searchParams.lang ?? languages?.defaultLanguage
  const langs = languages?.languages ?? []

  function setSearchParams(key: string, value: string | number | undefined | boolean) {
    navigate({
      to: '/recipes',
      search: (search) => ({
        [key]: value,
      }),
    })
  }

  const handleSearch = (text: string) => {
    navigate({
      to: '/recipes',
      search: (search) => ({
        ...search,
        searchText: text,
        page: 0,
      }),
    })
  }

  const handleEnergySort = (newValue: SortDescriptor) => {
    let sortOpts: any = {
      sortField: 'ENERGY_PER_SERVING',
      sortDirection: newValue.direction === 'ascending' ? 'ASC' : 'DESC',
    }
    let reset = false
    // Remove sort after descending
    if (sortDirection === 'descending') {
      sortOpts = {}
      reset = true
    }

    navigate({
      to: '/recipes',
      search: (search) => {
        if (reset) {
          delete search.sortField
          delete search.sortDirection
        }
        return {
          ...search,
          ...sortOpts,
          page: 0,
        }
      },
    })
  }

  return (
    <div className="RecipeIndexTable">
      <div className="RecipeIndexTable-header">
        <TableSearch handleSearch={handleSearch} defaultValue={searchParams.searchText} />

        {languages && (
          <Select
            value={{
              value: lang,
              label: languages.languages.find((l) => l.code === lang)?.name,
            }}
            options={languages.languages.map((lang) => ({
              value: lang.code,
              label: lang.name,
            }))}
            onChange={(selection) => setSearchParams('lang', selection?.value ?? '')}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                minWidth: '150px',
              }),
            }}
          />
        )}
      </div>

      <Table
        aria-label="Recipes table"
        onSortChange={handleEnergySort}
        sortDescriptor={{ column: sortField, direction: sortDirection }}>
        <TableHeader>
          <Column id="name" isRowHeader width="40%">
            {t('recipe.name')}
          </Column>
          <Column id="mealTypes">{t('recipe.mealType')}</Column>
          {langs.map((lang) => (
            <Column id={`lang-${lang.code}`} key={lang.code} width={40}>
              {lang.code.toUpperCase()}
            </Column>
          ))}
          <Column id="author">{t('recipe.author')}</Column>
          <Column id="caloriesPerServing" width={120} allowsSorting>
            {t('nutrition.caloriesPerServing')}
          </Column>
          <Column id="action"></Column>
        </TableHeader>
        <TableBody items={recipes} dependencies={[saveRecipe]}>
          {(recipe) => (
            <RecipeRow
              id={recipe.idRecipe}
              key={recipe.idRecipe}
              recipe={recipe}
              groupedTags={groupedTags}
              saveRecipe={saveRecipe}
              lang={searchParams.lang}
              languages={languages}
            />
          )}
        </TableBody>
      </Table>
      <TablePagination
        pagination={{
          totalCount: totalCount,
          page: searchParams.page ?? 0,
          setPage: (page) => {
            navigate({
              to: '/recipes',
              search: {
                ...searchParams,
                page,
              },
            })
          },
        }}
      />
    </div>
  )
}

export function Control({ children, ...props }: any) {
  return (
    <components.Control {...props}>
      <FontAwesomeIcon icon={faTags} color="var(--gray-400)" />
      {children}
    </components.Control>
  )
}
